import type React from 'react';
import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

import {
  EntryStatusEnum,
  EventTypeEnum,
  ParticipantTypeEnum,
  StageTypeEnum,
} from '@clubspark-node/clubspark-tennis-types';
import { useAtomValue } from 'jotai';
import { entryStatusConstants, positionActionConstants } from 'tods-competition-factory';

import { eventUnpairedDrawParticipantsAtomFamily } from 'atoms/events';
import CompetitorsActions, { type CompetitorsAvailableActions } from 'components/tables/competitors/CompetitorsActions';
import { useAddToTeamDialog } from 'containers/dialogs/AddToTeam';
import { useAssignParticipantDialog } from 'containers/dialogs/AssignParticipant';
import useWithdrawDialog from 'containers/dialogs/WithdrawDialog';
import useMutateTournament from 'hooks/tournament/useMutateTournament';
import type { Event, ParticipantInterface } from 'typedefs/store/tmxTypes';
import { noop } from 'utils/fnUtils';

const { SINGLES, DOUBLES, TEAM } = EventTypeEnum;
const { INDIVIDUAL, PAIR } = ParticipantTypeEnum;
const { MODIFY_PAIR_ASSIGNMENT } = positionActionConstants;
const { DIRECT_ENTRY_STATUSES, UNPAIRED, UNGROUPED } = entryStatusConstants;

export interface ParticipantActionsInterface {
  onPromote?: (participantIds: string[], entryStage: 'MAIN' | 'QUALIFYING') => void;
  onUnpairPlayers?: (participantId: string) => void;
  onAddToDraw?: (participantIds: string[]) => void;
  onRemoveFromDraw?: (participantIds: string[]) => void;
  onRemoveFromFlight?: (participantIds: string[]) => void;
  onReplaceDoublesPlayer?: (participantId: string) => void;
}

interface AvailableActionsProps {
  isMainDraw?: boolean;
  isQualifyingEnabled?: boolean;
  selectedParticipant: ParticipantInterface | Record<string, any>;
  selectedEvent?: Event;
  splitDraw?: boolean;
  isTeamAvailablePlayer?: boolean;
}

type CompetitorsPopoverContext = (
  selectedParticipant: ParticipantInterface,
  participantActions: ParticipantActionsInterface,
  target: HTMLElement,
  selectedEvent: Event,
  isQualifyingEnabled?: boolean,
  splitDraw?: boolean,
  isTeamAvailablePlayer?: boolean,
  isMainDraw?: boolean,
) => void;

const context = createContext<CompetitorsPopoverContext>(noop);

export const useCompetitorsPopover = () => useContext<CompetitorsPopoverContext>(context);

export const useAvailableActions = () => {
  return useCallback(
    ({
      selectedParticipant,
      isMainDraw,
      isQualifyingEnabled,
      isTeamAvailablePlayer,
      selectedEvent,
      splitDraw,
    }: AvailableActionsProps) => {
      return getAvailableActions({
        isMainDraw,
        isQualifyingEnabled,
        isTeamAvailablePlayer,
        selectedEvent,
        selectedParticipant,
        splitDraw,
      });
    },
    [],
  );
};

export const getAvailableActions = ({
  isMainDraw,
  isTeamAvailablePlayer,
  isQualifyingEnabled,
  selectedEvent,
  selectedParticipant,
  splitDraw,
}: AvailableActionsProps): CompetitorsAvailableActions => {
  const isTeamEvent = selectedEvent?.eventType === TEAM;
  const isDoublesEvent = selectedEvent?.eventType === DOUBLES;
  const isSinglesEvent = selectedEvent?.eventType === SINGLES;
  const drawExists = selectedEvent?.drawDefinitions?.length > 0;
  const eventId = selectedEvent?.eventId;

  const isAvailableUnpairedPlayers = selectedEvent?.entries?.some(
    (e) => e.entryStatus === UNPAIRED || e.entryStatus === UNGROUPED,
  );

  const participantType = selectedParticipant.participantType;
  const entry =
    selectedParticipant?.events
      ?.find((e) => e.eventId === eventId)
      ?.entries?.find((e) => e.participantId === selectedParticipant?.participantId) || {};
  const { entryStatus, entryStage } = entry;

  const isIndividual = participantType === INDIVIDUAL;
  const isPair = participantType === PAIR;
  const isTeam = participantType === TEAM;

  const isSelected = DIRECT_ENTRY_STATUSES.includes(entryStatus) && entryStage !== StageTypeEnum.QUALIFYING;
  const isQualifier = DIRECT_ENTRY_STATUSES.includes(entryStatus) && entryStage === StageTypeEnum.QUALIFYING;
  const isAlternate = entryStatus === EntryStatusEnum.ALTERNATE;
  const isWithdrawn = entryStatus === EntryStatusEnum.WITHDRAWN;

  return !isMainDraw
    ? {
        ADD_TO_DRAW: splitDraw && !isTeamEvent,
        MOVE_TO_ALTERNATES: !isDoublesEvent && isWithdrawn,
        MOVE_TO_AVAILABLE_PLAYERS: isTeamEvent && isIndividual && !isTeamAvailablePlayer,
        MOVE_TO_QUALIFYING:
          !isQualifier &&
          isQualifyingEnabled &&
          !drawExists &&
          isSelected &&
          (!isTeamEvent || (isTeamEvent && !isIndividual)),
        MOVE_TO_SELECTED: isQualifier && !drawExists,
        MOVE_TO_TEAM: isTeamEvent && isTeamAvailablePlayer,
        MOVE_TO_UNPAIRED: isWithdrawn && isDoublesEvent,
        PROMOTE_TO_SELECTED:
          !drawExists &&
          !isQualifier &&
          !isSelected &&
          ((isDoublesEvent && isPair) || (isSinglesEvent && isIndividual) || (isTeamEvent && isTeam)),
        PROMOTE_TO_QUALIFYING:
          !isQualifier &&
          !isSelected &&
          isQualifyingEnabled &&
          !drawExists &&
          ((isDoublesEvent && isPair) || (isSinglesEvent && isIndividual) || (isTeamEvent && isTeam)),
        REMOVE_FROM_DRAW: splitDraw && !isTeamEvent,
        REPLACE_DOUBLES_PLAYER: isDoublesEvent && isPair && isAvailableUnpairedPlayers && !isWithdrawn,
        UNPAIR_PLAYERS: isDoublesEvent && (isAlternate || isSelected || isQualifier),
        WITHDRAW_TO_ALTERNATES: isSelected || isQualifier || isTeamAvailablePlayer,
        WITHDRAW: !isWithdrawn && (!isDoublesEvent || (isDoublesEvent && isIndividual)),
      }
    : {
        REMOVE_FROM_FLIGHT: true,
      };
};

const CompetitorsPopover: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const [actions, setActions] = useState<CompetitorsAvailableActions>({});
  const [event, setEvent] = useState<Event>();
  const [moveParticipantActions, setMoveParticipantActions] = useState<ParticipantActionsInterface>({});
  const [open, setOpen] = useState<boolean>(false);
  const [participant, setParticipant] = useState<ParticipantInterface>();
  const unpairedDrawParticipants = useAtomValue(eventUnpairedDrawParticipantsAtomFamily(event?.eventId));
  const unpairedDrawParticipantsIds = useMemo(
    () => unpairedDrawParticipants.map((p) => p.participantId),
    [unpairedDrawParticipants],
  );
  const individualParticipants = useMemo(() => participant?.individualParticipants || [], [participant]);
  const individualParticipantsIds = useMemo(
    () => individualParticipants.map((p) => p.participantId),
    [individualParticipants],
  );

  const anchorEl = useRef<HTMLElement | null>();

  const getAvailableActions = useAvailableActions();
  const showAddToTeamDialog = useAddToTeamDialog();
  const showAssignParticipantDialog = useAssignParticipantDialog();
  const showWithdrawDecisionDialog = useWithdrawDialog();
  const [assignParticipant, isAssigningParticipant] = useMutateTournament('assignParticipant');
  const [moveTo] = useMutateTournament('moveTo');
  const [withdrawParticipantFromEvent] = useMutateTournament('withdrawParticipantFromEvent');

  const entryStatus = useMemo(
    () =>
      participant?.events
        ?.find((e) => e.eventId === event?.eventId)
        ?.entries?.find((e) => e.participantId === participant?.participantId)?.entryStatus,
    [participant, event],
  );

  const isTeam = event?.eventType === TEAM;
  const isAlternate = entryStatus === EntryStatusEnum.ALTERNATE;
  const isTeamIndividualParticipant = isTeam && participant?.participantType === INDIVIDUAL;

  const { onAddToDraw, onRemoveFromDraw, onPromote, onRemoveFromFlight, onUnpairPlayers } = moveParticipantActions;

  const handleParticipantActions = useCallback(
    (action, isArrayId = true) => {
      setOpen(false);
      participant && action(isArrayId ? [participant?.participantId] : participant?.participantId);
    },
    [participant],
  );

  const handlePromoteToMain = useCallback(() => {
    setOpen(false);
    participant && onPromote([participant?.participantId], StageTypeEnum.MAIN);
  }, [participant, onPromote]);

  const handlePromoteToQualifying = useCallback(() => {
    setOpen(false);
    participant && onPromote([participant?.participantId], StageTypeEnum.QUALIFYING);
  }, [participant, onPromote]);

  const handleMoveToQualifying = useCallback(() => {
    setOpen(false);
    participant &&
      event &&
      moveTo?.([participant.participantId], event, isTeamIndividualParticipant, StageTypeEnum.QUALIFYING);
  }, [participant, event, moveTo, isTeamIndividualParticipant]);

  const handleMoveToSelected = useCallback(() => {
    setOpen(false);
    participant &&
      event &&
      moveTo?.([participant.participantId], event, isTeamIndividualParticipant, StageTypeEnum.MAIN);
  }, [participant, event, moveTo, isTeamIndividualParticipant]);

  const handleMoveToTeam = useCallback(() => {
    setOpen(false);
    participant && event && showAddToTeamDialog([participant], false, event.eventId);
  }, [participant, showAddToTeamDialog, event]);

  const handleMoveToAlternates = useCallback(() => {
    setOpen(false);
    participant && event && moveTo?.([participant.participantId], event, isTeamIndividualParticipant);
  }, [isTeamIndividualParticipant, moveTo, participant, event]);

  const handleReplaceDoublesPlayer = useCallback(() => {
    setOpen(false);
    showAssignParticipantDialog({
      loading: isAssigningParticipant,
      payload: [
        {
          availableIndividualParticipantIds: unpairedDrawParticipantsIds,
          availableIndividualParticipants: unpairedDrawParticipants,
          existingIndividualParticipantIds: individualParticipantsIds,
          existingIndividualParticipants: individualParticipants,
          isAlternate,
          method: 'modifyPairAssignment',
          payload: {
            eventId: event?.eventId,
            participantId: participant?.participantId,
          },
          type: MODIFY_PAIR_ASSIGNMENT,
        },
      ],
      onSave: (payload) => assignParticipant(payload),
    });
  }, [
    assignParticipant,
    event?.eventId,
    individualParticipants,
    individualParticipantsIds,
    isAlternate,
    isAssigningParticipant,
    participant?.participantId,
    showAssignParticipantDialog,
    unpairedDrawParticipants,
    unpairedDrawParticipantsIds,
  ]);

  const handleWithdrawal = useCallback(
    async (moveTo?: 'ALTERNATE' | 'WITHDRAWN' | 'UNGROUPED') => {
      setOpen(false);
      if (moveTo === EntryStatusEnum.ALTERNATE) {
        const entryStatus = isTeamIndividualParticipant ? EntryStatusEnum.UNGROUPED : EntryStatusEnum.ALTERNATE;
        participant && event && withdrawParticipantFromEvent([participant.participantId], entryStatus, event.eventId);
      } else {
        const withdrawDecision = await showWithdrawDecisionDialog(isTeamIndividualParticipant, isAlternate, isTeam);
        withdrawDecision &&
          event &&
          participant &&
          withdrawParticipantFromEvent([participant.participantId], EntryStatusEnum.WITHDRAWN, event?.eventId);
      }
    },
    [
      event,
      isAlternate,
      isTeam,
      isTeamIndividualParticipant,
      participant,
      showWithdrawDecisionDialog,
      withdrawParticipantFromEvent,
    ],
  );

  const contextValue = useCallback<CompetitorsPopoverContext>(
    (
      selectedParticipant,
      participantActions,
      target,
      selectedEvent,
      isQualifyingEnabled,
      splitDraw,
      isTeamAvailablePlayer,
      isMainDraw,
    ) => {
      anchorEl.current = target;
      setParticipant(selectedParticipant);
      setActions(
        getAvailableActions({
          isMainDraw,
          isQualifyingEnabled,
          isTeamAvailablePlayer,
          selectedEvent,
          splitDraw,
          selectedParticipant,
        }),
      );
      setMoveParticipantActions(participantActions);
      setEvent(selectedEvent);
      setOpen(true);
    },
    [getAvailableActions],
  );

  const handleClose = useCallback(() => setOpen(false), []);

  return useMemo(
    () => (
      <context.Provider value={contextValue}>
        {children}
        <CompetitorsActions
          actions={actions}
          anchorEl={() => anchorEl.current}
          open={open}
          onAddToDraw={() => handleParticipantActions(onAddToDraw)}
          onClose={handleClose}
          onMoveToAlternates={handleMoveToAlternates}
          onMoveToQualifying={handleMoveToQualifying}
          onMoveToSelected={handleMoveToSelected}
          onMoveToTeam={handleMoveToTeam}
          onPromoteToMain={handlePromoteToMain}
          onPromoteToQualifying={handlePromoteToQualifying}
          onRemoveFromDraw={() => handleParticipantActions(onRemoveFromDraw)}
          onRemoveFromFlight={() => handleParticipantActions(onRemoveFromFlight)}
          onReplaceDoublesPlayer={handleReplaceDoublesPlayer}
          onUnpairPlayers={() => handleParticipantActions(onUnpairPlayers, false)}
          onWithdrawal={() => handleWithdrawal(EntryStatusEnum.WITHDRAWN)}
          onWithdrawToAlternates={() => handleWithdrawal(EntryStatusEnum.ALTERNATE)}
        />
      </context.Provider>
    ),
    [
      contextValue,
      children,
      actions,
      open,
      handleClose,
      handleMoveToAlternates,
      handleMoveToQualifying,
      handleMoveToSelected,
      handleMoveToTeam,
      handlePromoteToMain,
      handlePromoteToQualifying,
      handleParticipantActions,
      onAddToDraw,
      onRemoveFromDraw,
      onRemoveFromFlight,
      handleReplaceDoublesPlayer,
      onUnpairPlayers,
      handleWithdrawal,
    ],
  );
};

export default CompetitorsPopover;
